import * as React from 'react'
import { Link } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import { useClientsList } from './hooks'
import { CCardBody } from '@coreui/react-pro'
import { path } from '../../../libs'
import {
  Button,
  DatePicker,
  Flex,
  Input,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import {
  CrownOutlined,
  ExperimentOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Client } from './types'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { transformCountriesToOptions } from '../../../libs/formatCountries'
import { useUnit } from 'effector-react'
import { $countriesList } from '../../../features/dictionaries/model'
import { useUrlFilters } from '../../../libs/useUrlFilters'
import Highlighter from 'react-highlight-words'

const { Text } = Typography
const { RangePicker } = DatePicker

// ── Filter config ──────────────────────────────────────────
const FILTER_CONFIG = {
  clientId: { type: 'string' as const },
  email: { type: 'string' as const },
  tsname: { type: 'string' as const },
  documentNumber: { type: 'string' as const },
  mtLogin: { type: 'string' as const },
  dateFrom: { type: 'string' as const },
  dateTo: { type: 'string' as const },
  partner: { type: 'boolean' as const },
  verified: { type: 'boolean' as const },
  hasDeposit: { type: 'boolean' as const },
  country: { type: 'array' as const },
  products: { type: 'array' as const },
  page: { type: 'string' as const, default: '1', pagination: true },
  pageSize: { type: 'string' as const, default: '15', pagination: true },
  sortField: { type: 'string' as const, default: 'id', pagination: true },
  sortOrder: { type: 'string' as const, default: 'descend', pagination: true },
}

const PRODUCT_OPTIONS = [
  { label: 'Headway', value: 'headway' },
  { label: 'Nova', value: 'nova' },
]

/** Yes / No toggle — click active value again to reset */
function BoolFilter({
  label,
  value,
  onChange,
}: {
  label: string
  value: boolean | undefined
  onChange: (v: boolean | undefined) => void
}) {
  return (
    <Flex align="center" gap={6}>
      <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{label}</Text>
      <Button
        type={value === true ? 'primary' : 'default'}
        onClick={() => onChange(value === true ? undefined : true)}
        style={{ fontSize: 12, padding: '0 12px' }}
      >
        Yes
      </Button>
      <Button
        type={value === false ? 'primary' : 'default'}
        danger={value === false}
        onClick={() => onChange(value === false ? undefined : false)}
        style={{ fontSize: 12, padding: '0 12px' }}
      >
        No
      </Button>
    </Flex>
  )
}

export const ClientsList = () => {
  const { clients, totalCountOfClients, loading, onChangeClientsList } =
    useClientsList()
  const countriesList = useUnit($countriesList)
  const { filters, setFilters, resetFilters } =
    useUrlFilters(FILTER_CONFIG)

  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || 15
  const orderBy = filters.sortField || 'id'
  const orderDirection = filters.sortOrder === 'ascend' ? '' : '-'


  // Local input state — applied to URL only on Enter/Search
  const [inputClientId, setInputClientId] = useState(filters.clientId || '')
  const [inputEmail, setInputEmail] = useState(filters.email || '')
  const [inputName, setInputName] = useState(filters.tsname || '')
  const [inputDoc, setInputDoc] = useState(filters.documentNumber || '')
  const [inputMt, setInputMt] = useState(filters.mtLogin || '')

  // ── Build API filters from URL state ────────────────────
  const buildApiFilters = useCallback(() => {
    const f: Record<string, any> = {}
    // Text search
    if (filters.clientId) f.id = filters.clientId
    if (filters.email) f.email = filters.email
    if (filters.tsname) f.tsname = filters.tsname
    if (filters.documentNumber) f.documentNumber = filters.documentNumber
    if (filters.mtLogin) f.mtLogin = filters.mtLogin
    // Boolean
    if (filters.partner !== undefined) f.partner = filters.partner
    if (filters.verified !== undefined) f.verified = filters.verified
    if (filters.hasDeposit !== undefined) f.hasDeposit = filters.hasDeposit
    // Arrays
    if (filters.country && filters.country.length > 0)
      f.country = filters.country
    if (filters.products && filters.products.length > 0)
      f.products = filters.products
    // Date range
    if (filters.dateFrom) f.dateFrom = filters.dateFrom
    if (filters.dateTo) f.dateTo = filters.dateTo
    return Object.keys(f).length > 0 ? f : undefined
  }, [filters])

  // ── Fetch data when filters change ──────────────────────
  useEffect(() => {
    onChangeClientsList({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      filters: buildApiFilters(),
    })
  }, [filters, page, pageSize, orderBy, orderDirection])

  // ── Apply text search (validates min length) ────────────
  const applySearch = (
    key: 'clientId' | 'email' | 'tsname' | 'documentNumber' | 'mtLogin',
    value: string,
  ) => {
    const v = value.trim()

    // Min length requirements from docs
    if (key === 'clientId' || key === 'email' || key === 'tsname') {
      if (v.length > 0 && v.length < 3) return // need at least 3 chars
    }
    if (key === 'mtLogin') {
      if (v.length > 0 && (v.length < 4 || v.length > 10)) return // 4-10 digits
    }

    setFilters({ [key]: v || undefined, page: undefined } as any)
  }

  const clearSearch = (
    key: 'clientId' | 'email' | 'tsname' | 'documentNumber' | 'mtLogin',
    setter: (v: string) => void,
  ) => {
    setter('')
    setFilters({ [key]: undefined, page: undefined } as any)
  }

  // ── Country options for filter ──────────────────────────
  const countryOptions = React.useMemo(
    () =>
      transformCountriesToOptions(countriesList).map(
        (c: { text: string; value: string }) => ({
          label: c.text,
          value: c.value,
        }),
      ),
    [countriesList],
  )

  const countryNameByCode = React.useMemo(() => {
    const map: Record<string, string> = {}
    countryOptions.forEach((c) => {
      map[c.value] = c.label as string
    })
    return map
  }, [countryOptions])

  // ── Date range from URL ─────────────────────────────────
  const dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null =
    filters.dateFrom && filters.dateTo
      ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)]
      : null

  // ── Active filter tags ─────────────────────────────────
  const activeTags = React.useMemo(() => {
    const tags: { key: string; label: string; clear: () => void }[] = []
    if (filters.clientId)
      tags.push({ key: 'clientId', label: `ID: ${filters.clientId}`, clear: () => clearSearch('clientId', setInputClientId) })
    if (filters.email)
      tags.push({ key: 'email', label: `Email: ${filters.email}`, clear: () => clearSearch('email', setInputEmail) })
    if (filters.tsname)
      tags.push({ key: 'tsname', label: `Name: ${filters.tsname}`, clear: () => clearSearch('tsname', setInputName) })
    if (filters.documentNumber)
      tags.push({ key: 'doc', label: `Doc: ${filters.documentNumber}`, clear: () => clearSearch('documentNumber', setInputDoc) })
    if (filters.mtLogin)
      tags.push({ key: 'mt', label: `MT: ${filters.mtLogin}`, clear: () => clearSearch('mtLogin', setInputMt) })
    if (filters.partner !== undefined)
      tags.push({ key: 'partner', label: `Partner: ${filters.partner ? 'Yes' : 'No'}`, clear: () => setFilters({ partner: undefined, page: undefined } as any) })
    if (filters.verified !== undefined)
      tags.push({ key: 'verified', label: `Verified: ${filters.verified ? 'Yes' : 'No'}`, clear: () => setFilters({ verified: undefined, page: undefined } as any) })
    if (filters.hasDeposit !== undefined)
      tags.push({ key: 'deposit', label: `Deposit: ${filters.hasDeposit ? 'Yes' : 'No'}`, clear: () => setFilters({ hasDeposit: undefined, page: undefined } as any) })
    if (filters.dateFrom && filters.dateTo)
      tags.push({ key: 'date', label: `${filters.dateFrom} \u2014 ${filters.dateTo}`, clear: () => setFilters({ dateFrom: undefined, dateTo: undefined, page: undefined } as any) })
    filters.country?.forEach((c) =>
      tags.push({ key: `c-${c}`, label: countryNameByCode[c] || c, clear: () => setFilters({ country: filters.country?.filter((x) => x !== c), page: undefined } as any) }),
    )
    filters.products?.forEach((p) =>
      tags.push({ key: `p-${p}`, label: p, clear: () => setFilters({ products: filters.products?.filter((x) => x !== p), page: undefined } as any) }),
    )
    return tags
  }, [filters])

  // ── Columns ─────────────────────────────────────────────
  const columns: ColumnsType<Client> = [
    {
      title: 'ID',
      dataIndex: 'clientId',
      sorter: true,
      width: 130,
      render: (_: any, item: Client) => (
        <Flex align="center" gap={4} wrap="wrap" justify="center">
          <Link
            to={path.clients.details(item.clientId)}
            target="_blank"
            style={{ fontSize: 14, fontWeight: 600 }}
          >
            <Highlighter
              searchWords={[inputClientId.trim()]}
              highlightStyle={{
                backgroundColor: 'var(--color-yellow-50) ',
                padding: 0,
              }}
              autoEscape
              textToHighlight={String(item.clientId)}
            />
          </Link>
          {item.isTest && (
            <Tag
              icon={<ExperimentOutlined />}
              color="purple"
              style={{ fontSize: 10, margin: 0, padding: '0 4px' }}
            >
              Test
            </Tag>
          )}
          {item.isPremium && (
            <Tag
              icon={<CrownOutlined />}
              color="gold"
              style={{ fontSize: 10, margin: 0, padding: '0 4px' }}
            >
              Premium
            </Tag>
          )}
        </Flex>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      sorter: true,
      width: 210,
      render: (v: string) => (
        <Tooltip title={v}>
          <Text style={{ fontSize: 14 }}>
            <Highlighter
              searchWords={[inputEmail.trim()]}
              highlightStyle={{
                backgroundColor: 'var(--color-yellow-50)',
                padding: 0,
              }}
              autoEscape
              textToHighlight={v ?? ''}
            />
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Full name',
      dataIndex: 'fullName',
      width: 190,
      render: (v: string) => {
        const normalizeSpaces = (str: string) => str.replace(/\s+/g, ' ').trim();
        return <Text strong style={{ fontSize: 14 }}>
          <Highlighter
            searchWords={[normalizeSpaces(inputName)]}
            highlightStyle={{
              backgroundColor: 'var(--color-yellow-50)',
              padding: 0,
            }}
            autoEscape
            textToHighlight={v || '\u2014'}
          />
        </Text>
      },
    },
    {
      title: 'Partner',
      dataIndex: 'partner',
      sorter: true,
      width: 90,
      align: 'center',
      render: (v: boolean) =>
        v ? (
          <Tag color="gold" style={{ fontSize: 10, fontWeight: 700 }}>
            YES
          </Tag>
        ) : (
          <Tag color="red" style={{ fontSize: 10, fontWeight: 700 }}>
            NO
          </Tag>
        ),
    },
    {
      title: 'Verified',
      dataIndex: 'verified',
      sorter: true,
      width: 100,
      align: 'center',
      render: (v: boolean) =>
        v ? (
          <Tag color="success" style={{ fontSize: 10, fontWeight: 700 }}>
            YES
          </Tag>
        ) : (
          <Tag color="red" style={{ fontSize: 10, fontWeight: 700 }}>
            NO
          </Tag>
        ),
    },
    {
      title: 'Document \u2116',
      dataIndex: 'documentNumber',
      width: 140,
      render: (v: string) =>
        v ? (
          <Text style={{ fontSize: 14 }}>
            <Highlighter
              searchWords={[inputDoc.trim()]}
              highlightStyle={{
                backgroundColor: 'var(--color-yellow-50)',
                padding: 0,
              }}
              autoEscape
              textToHighlight={v ?? ''}
            />
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 14 }}>
            {'\u2014'}
          </Text>
        ),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      sorter: true,
      width: 110,
      render: (v: string) => (
        <Tooltip title={v}>
          <Text style={{ fontSize: 14 }}>{countryNameByCode[v] || v}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Product',
      dataIndex: 'products',
      sorter: true,
      width: 150,
      render: (v: string[]) => (
        <Flex gap={4} wrap="wrap">
          {v?.map((p) => (
            <Tag
              key={p}
              color={p === 'headway' ? '#dbb454' : '#50f9ac'}
              style={{
                fontSize: 10,
                margin: 0,
                textTransform: 'capitalize',
              }}
            >
              {p}
            </Tag>
          ))}
        </Flex>
      ),
    },
    {
      title: 'Registered',
      dataIndex: 'registeredAt',
      sorter: true,
      width: 140,
      render: (_: any, record: Client) => {
        const dateStr = record.humanRegisteredAt || record.registeredAt
        return (
          <Tooltip title={record.registeredAt}>
            <div>
              <Text style={{ fontSize: 14 }}>
                {dayjs(dateStr).format('DD MM YYYY')}
              </Text>{' '}
              <Text
                type="secondary"
                style={{ fontSize: 12 }}
              >
                {dayjs(dateStr).format('HH:mm')}
              </Text>
            </div>
          </Tooltip>
        )
      },
    },
    {
      title: 'Deposit',
      dataIndex: 'hasDeposit',
      sorter: true,
      width: 90,
      align: 'center',
      render: (v: boolean) =>
        v ? (
          <Tag color="success" style={{ fontSize: 10, fontWeight: 700 }}>
            YES
          </Tag>
        ) : (
          <Tag color="red" style={{ fontSize: 10, fontWeight: 700 }}>
            NO
          </Tag>
        ),
    },
  ]

  return (
    <CCardBody>
      {/* ── Filters panel ─────────────────────────────────── */}
      <div
        style={{
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 12,
          border: '1px solid var(--crm-border, #d9d9d9)',
          background: 'var(--crm-bg, #fff)',
        }}
      >
        {/* Row 1: text search fields */}
        <Flex gap={6} wrap="wrap" style={{ marginBottom: 10 }}>
          <Input
            prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
            placeholder="Client ID"
            value={inputClientId}
            onChange={(e) => setInputClientId(e.target.value.replace(/\D/g, ''))}
            onPressEnter={() => applySearch('clientId', inputClientId)}
            onBlur={() => applySearch('clientId', inputClientId)}
            allowClear
            onClear={() => clearSearch('clientId', setInputClientId)}
            style={{ width: 120, fontSize: 14 }}
          />
          <Input
            prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
            placeholder="Email"
            value={inputEmail}
            onChange={(e) => setInputEmail(e.target.value)}
            onPressEnter={() => applySearch('email', inputEmail)}
            onBlur={() => applySearch('email', inputEmail)}
            allowClear
            onClear={() => clearSearch('email', setInputEmail)}
            style={{ width: 200 }}
          />
          <Input
            prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
            placeholder="Full name"
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            onPressEnter={() => applySearch('tsname', inputName)}
            onBlur={() => applySearch('tsname', inputName)}
            allowClear
            onClear={() => clearSearch('tsname', setInputName)}
            style={{ width: 180 }}
          />
          <Input
            prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
            placeholder="Document №"
            value={inputDoc}
            onChange={(e) => setInputDoc(e.target.value)}
            onPressEnter={() => applySearch('documentNumber', inputDoc)}
            onBlur={() => applySearch('documentNumber', inputDoc)}
            allowClear
            onClear={() => clearSearch('documentNumber', setInputDoc)}
            style={{ width: 150, fontSize: 14 }}
          />
          <Input
            prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
            placeholder="MT Login (4-10 digits)"
            value={inputMt}
            onChange={(e) => setInputMt(e.target.value.replace(/\D/g, '').slice(0, 10))}
            onPressEnter={() => applySearch('mtLogin', inputMt)}
            onBlur={() => applySearch('mtLogin', inputMt)}
            allowClear
            onClear={() => clearSearch('mtLogin', setInputMt)}
            style={{ width: 200, fontSize: 14 }}
          />
        </Flex>

        {/* Divider */}
        <div style={{ height: 1, background: 'var(--crm-border, #e2e5ec)', margin: '0 0 10px' }} />

        {/* Row 2: boolean segmented + select filters */}
        <Flex gap={10} wrap="wrap" align="center">
          <BoolFilter label="Partner" value={filters.partner} onChange={(v) => { setFilters({ partner: v, page: undefined } as any) }} />
          <BoolFilter label="Verified" value={filters.verified} onChange={(v) => { setFilters({ verified: v, page: undefined } as any) }} />
          <BoolFilter label="Deposit" value={filters.hasDeposit} onChange={(v) => { setFilters({ hasDeposit: v, page: undefined } as any) }} />

          <div style={{ width: 1, height: 28, background: 'var(--crm-border, #e2e5ec)' }} />

          <Select
            mode="multiple"
            placeholder="Country"
            value={filters.country || []}
            onChange={(v) => { setFilters({ country: v.length > 0 ? v : undefined, page: undefined } as any) }}
            options={countryOptions}
            style={{ minWidth: 180 }}
            maxTagCount="responsive"
            allowClear
            showSearch
            optionFilterProp="label"
          />

          <Select
            mode="multiple"
            placeholder="Product"
            value={filters.products || []}
            onChange={(v) => { setFilters({ products: v.length > 0 ? v : undefined, page: undefined } as any) }}
            options={PRODUCT_OPTIONS}
            style={{ minWidth: 140 }}
            maxTagCount="responsive"
            allowClear
          />

          <RangePicker
            value={dateRange}
            onChange={(vals) => {
              if (vals && vals[0] && vals[1]) {
                setFilters({
                  dateFrom: vals[0].format('YYYY-MM-DD'),
                  dateTo: vals[1].format('YYYY-MM-DD'),
                  page: undefined,
                } as any)
              } else {
                setFilters({ dateFrom: undefined, dateTo: undefined, page: undefined } as any)
              }
            }}
            format="YYYY-MM-DD"
            placeholder={['Registered from', 'to']}
            style={{ width: 260 }}
          />
        </Flex>

        {/* Row 3: active filter tags */}
        {activeTags.length > 0 && (
          <>
            <div style={{ height: 1, background: 'var(--crm-border, #e2e5ec)', margin: '10px 0 8px' }} />
            <Flex gap={6} wrap="wrap" align="center">
              <Text
                type="secondary"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                }}
              >
                Active:
              </Text>
              {activeTags.map((t) => (
                <Tag
                  key={t.key}
                  closable
                  onClose={t.clear}
                  style={{ fontSize: 11, margin: 0 }}
                  color="blue"
                >
                  {t.label}
                </Tag>
              ))}
              <Button
                size="small"
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => {
                  resetFilters()
                  setInputClientId('')
                  setInputEmail('')
                  setInputName('')
                  setInputDoc('')
                  setInputMt('')
                }}
                style={{ color: '#ef4444', fontSize: 11 }}
              >
                Reset all
              </Button>
            </Flex>
          </>
        )}
      </div>

      {/* ── Table ─────────────────────────────────────────── */}
      <Table
        columns={columns}
        dataSource={clients}
        size="small"
        scroll={{ x: 1260 }}
        loading={loading}
        rowKey="clientId"
        rowClassName={(_record, index) =>
          index % 2 === 1 ? 'ant-table-row-striped' : ''
        }
        title={() => (
          <Button
            size="small"
            style={{ marginLeft: 'auto', display: 'block' }}
            onClick={() =>
              onChangeClientsList({
                limit: pageSize,
                page,
                orderBy,
                orderDirection,
                filters: buildApiFilters(),
              })
            }
          >
            Update Data
          </Button>
        )}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: [15, 30, 50, 100],
          total: totalCountOfClients,
          showTotal: (t, [f, to]) => `${f}\u2013${to} of ${t}`,
          size: 'default',
          position: ['topRight', 'bottomRight'],
        }}
        onChange={(pagination, _filters, sorter) => {
          const updates: Record<string, any> = {}
          if (pagination.current) updates.page = pagination.current === 1 ? undefined : String(pagination.current)
          if (pagination.pageSize && pagination.pageSize !== pageSize) {
            updates.pageSize = String(pagination.pageSize)
            updates.page = undefined
          }
          // @ts-ignore
          if (sorter.field) {
            // @ts-ignore
            const field = sorter.field === 'clientId' ? 'id' : sorter.field
            updates.sortField = field === 'id' ? undefined : field
            // @ts-ignore
            updates.sortOrder = sorter.order === 'descend' ? undefined : 'ascend'
          }
          setFilters(updates as any)
        }}
      />
      <style>{`
        .ant-table-row-striped td {
          background: rgba(0, 0, 0, 0.02) !important;
        }
        .ant-table-cell { vertical-align: middle !important; }
      `}</style>
    </CCardBody>
  )
}
